import { registerSW } from 'virtual:pwa-register';

/**
 * Service-worker registration and update loop.
 *
 * Why this file exists
 * ───────────────────
 * Registration used to be a side effect of importing `virtual:pwa-register`
 * inside PwaUpdatePrompt.vue. That component was deleted in 5a06ca7 to get
 * rid of the "New version available" toast, and the registration went with
 * it — leaving whatever service worker was already installed on each device
 * in charge, with nothing ever asking it to update. Deployed frontends kept
 * serving the cached bundle from whenever the user first installed the app.
 *
 * Registering explicitly here (rather than relying on the plugin's implicit
 * script injection) keeps that link visible in the source, and lets us drive
 * the update checks below.
 *
 * How updating works now
 * ──────────────────────
 * `registerType: 'autoUpdate'` + `skipWaiting` + `clientsClaim` (vite.config.ts)
 * mean a newly-deployed worker installs, activates, and claims open tabs
 * without waiting — and vite-plugin-pwa reloads the page once it takes over.
 * The missing half was *noticing* a new deploy: browsers only check for a
 * new worker on navigation, and an installed PWA can stay open for days
 * without one. So we poll.
 */

// How often to ask the browser to re-fetch sw.js and compare it.
const UPDATE_INTERVAL_MS = 60 * 60 * 1000; // 1 hour
// Don't hammer the server if several triggers fire at once.
const MIN_CHECK_GAP_MS = 60 * 1000;

let lastCheck = 0;

export function initServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;

  registerSW({
    immediate: true,
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;

      const check = async (): Promise<void> => {
        // A check while offline just throws; the `online` listener below
        // re-runs it the moment the connection is back.
        if (!navigator.onLine) return;
        const now = Date.now();
        if (now - lastCheck < MIN_CHECK_GAP_MS) return;
        lastCheck = now;
        try {
          await registration.update();
        } catch {
          // Transient — the next trigger will retry.
        }
      };

      setInterval(check, UPDATE_INTERVAL_MS);

      // Reopening the app (or switching back to the tab) is the moment a
      // user most expects to be on the latest build.
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') void check();
      });
      window.addEventListener('online', () => void check());
      window.addEventListener('focus', () => void check());
    },
    onRegisterError(error) {
      console.error('[pwa] service worker registration failed', error);
    },
  });
}
